<!-- frontend/src/app/app.html -->
@if (showUpdateNotice()) {
  <div
    class="update-notice-backdrop"
    role="button"
    tabindex="0"
    (click)="dismissUpdateNotice()"
    (keydown.enter)="dismissUpdateNotice()"
    (keydown.escape)="dismissUpdateNotice()"
    [attr.aria-label]="'app.update_notice.dismiss_aria' | transloco"
  >
    <div
      class="update-notice"
      role="alertdialog"
      aria-labelledby="notice-title"
      aria-describedby="notice-desc"
      (click)="$event.stopPropagation()"
      (keydown.enter)="$event.stopPropagation()"
    >
      <div class="update-notice__accent"></div>
      <div class="update-notice__content">
        <div class="update-notice__icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 id="notice-title" class="update-notice__title">
          {{ 'app.update_notice.title' | transloco }}
        </h2>
        <p id="notice-desc" class="update-notice__message">
          {{ 'app.update_notice.body' | transloco }}
        </p>
        <p class="update-notice__action-hint">
          {{ 'app.update_notice.action_hint' | transloco }}
        </p>
        <button class="update-notice__btn" (click)="dismissUpdateNotice()" type="button">
          {{ 'app.update_notice.dismiss' | transloco }}
        </button>
      </div>
    </div>
  </div>
}

<!--
  Single, FIXED-position `<router-outlet>`. Everything around it is
  conditionally rendered or hidden via CSS, but the outlet itself
  never moves between branches. That's the whole point: when the
  outlet is destroyed by a template re-evaluation (e.g. branch swap
  inside an `@if/@else` chain), Angular tears down whichever component
  was rendering inside it. For routes whose auth state shifts mid-flow
  — `/recover-mfa` specifically — that destruction silently re-mounts
  the route with default signals, breaking the recovery. Keep this
  template structurally stable.
-->
<div
  class="app-container"
  [class.app-container--shell]="showSidebar()"
  [class.app-container--bare]="!showSidebar()"
  [class.app-container--sidebar-closed]="showSidebar() && !isSidebarOpen()"
>
  <!-- Shell layout: sidebar floor-to-ceiling on the left; main column
       floats as a Linear-style `--bg-main` canvas (inset + radius; no
       separate topbar strip). Toggle is shell-owned (far left of
       Veritise) so it stays put when the rail fully retracts. Sidebar
       owns brand, nav, org, Import Agent. Full-screen routes host
       their own identity bars. -->
  <div class="app-body">
    @if (showSidebar()) {
      <button
        type="button"
        class="sidebar-chrome-toggle"
        (click)="toggleSidebar()"
        aria-controls="app-shell-sidebar"
        [attr.aria-label]="
          (isSidebarOpen() ? 'nav.collapse_sidebar' : 'nav.expand_sidebar') | transloco
        "
        [attr.aria-expanded]="isSidebarOpen()"
        [matTooltip]="(isSidebarOpen() ? 'nav.collapse_sidebar' : 'nav.expand_sidebar') | transloco"
        matTooltipPosition="right"
      >
        <app-action-glyph name="sidebar-toggle" />
      </button>
      <app-sidebar id="app-shell-sidebar" />
    }
    <div class="app-main-column">
      <main class="main-content">
        <router-outlet></router-outlet>
      </main>
    </div>
  </div>
</div>

@if (showBootError()) {
  <!-- Boot is wedged (≥25s without `BootHealthService.isBooting()`
       flipping false). Replace the spinner-only loader with a panel
       the user can act on — Reset & reload (nuclear) / Reload /
       Keep waiting. Diagnostic readout of which signal is stuck is
       included for the screenshotting-affected-user case. -->
  <app-boot-error
    [stuckSignals]="bootHealth.stuckSignals()"
    [logoSrc]="logoSrc()"
    (keepWaiting)="onBootErrorKeepWaiting()"
    (reload)="onBootErrorReload()"
  />
} @else if (showBootLoader()) {
  <!-- Loader OVERLAY (z-index above the outlet). Replaces the previous
       branch-swap loader: now the outlet behind it keeps rendering its
       component, but the user just sees the loader on top until boot
       is fully resolved. Suppressed while the recovery flow is active
       so the recovery component is visible immediately. The slowHint
       (rendered at ~12s in) is the gentle pre-deadline signal that we
       know boot is taking a while. -->
  <app-loader [fullScreen]="true" [logoSrc]="logoSrc()" [slowHint]="showSlowHint()" />
}

@if (buildStale()) {
  <!-- "New version available" banner. `BuildVersionCheckService` polls
       `index.html` with `cache: 'reload'` and flips this signal when
       the deployed hashed main bundle differs from the one we loaded
       with. We surface a banner with a Reload button rather than
       auto-reloading, so in-progress work isn't lost. -->
  <div class="build-update-banner" role="status" aria-live="polite">
    <span class="build-update-banner__text">{{ 'app.build_banner.text' | transloco }}</span>
    <button type="button" class="build-update-banner__btn" (click)="reloadForUpdate()">
      {{ 'app.build_banner.reload' | transloco }}
    </button>
  </div>
}
